import { Box, Typography } from '@mui/material';
import { useState } from 'react';

interface ClientPhotoProps {
    /** The photo's URL, already resolved against the image host; absent while none is selected. */
    photoUrl?: string | null;
    /** The client's name, used as the image's alternative text. */
    clientName?: string | null;
}

/**
 * The selected client's photo, or a "No Photo" placeholder in the same frame.
 *
 * The URL that failed to load is remembered here rather than on the page: it is of no interest
 * to anything else, and a photo that 404s must fall back to the placeholder without the page
 * re-rendering around it. A URL is retried whenever it changes, since the `key` remounts the
 * image and a client whose photo was fixed should show it again.
 */
const ClientPhoto = ({ photoUrl, clientName }: ClientPhotoProps) => {
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const hasPhoto = !!photoUrl && failedUrl !== photoUrl;

    return (
        <Box
            sx={{
                width: '150px',
                height: '170px',
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
            }}
        >
            {hasPhoto ? (
                <Box
                    key={photoUrl}
                    component="img"
                    src={photoUrl}
                    alt={clientName ?? ''}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onLoad={() => setFailedUrl(null)}
                    onError={() => setFailedUrl(photoUrl)}
                />
            ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    No Photo
                </Typography>
            )}
        </Box>
    );
};

export default ClientPhoto;
